import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import airtableService from '../utils/airtable';

/**
 * BlobServe Component with Access Control
 * Serves files from localStorage in development mode with access control checks
 * In production, files are served directly from Vercel Blob Storage
 * Implements 4-tier access control: Public, Authenticated, Private, Admin Only
 */
const BlobServe = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAccess } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [fileMetadata, setFileMetadata] = useState(null);

  useEffect(() => {
    const checkAccessAndServeFile = async () => {
      try {
        setLoading(true);
        setError(null);
        setAccessDenied(false);

        console.log(`[BlobServe] Checking access for file hash: ${hash}`);

        // Step 1: Get file metadata from Airtable
        const metadataResponse = await airtableService.getContentFileByHash(hash);

        if (!metadataResponse.success) {
          console.error('[BlobServe] File not found in Airtable:', metadataResponse.error);
          setError('File not found');
          setLoading(false);
          return;
        }

        const metadata = metadataResponse.data;
        setFileMetadata(metadata);

        console.log('[BlobServe] File metadata:', {
          fileName: metadata.fileName,
          accessLevel: metadata.accessLevel,
          status: metadata.status
        });

        // Step 2: Check if file is active
        if (metadata.status !== 'Active') {
          console.warn('[BlobServe] File is not active:', metadata.status);
          setError('This file is no longer available');
          setLoading(false);
          return;
        }

        // Step 3: Check access permissions
        const hasAccess = checkAccess(metadata.accessLevel);

        console.log('[BlobServe] Access check result:', {
          accessLevel: metadata.accessLevel,
          isAuthenticated,
          userRole: user?.userRole,
          hasAccess
        });

        // Step 4: Log the access attempt
        await airtableService.logAccess({
          fileHash: hash,
          fileName: metadata.fileName,
          username: user?.username || 'Anonymous',
          userRole: user?.userRole || 'Viewer',
          action: 'View',
          accessResult: hasAccess ? 'Granted' : 'Denied',
          ipAddress: 'Unknown', // Could be enhanced with IP detection
          userAgent: navigator.userAgent,
          notes: hasAccess ? '' : `Access denied for level: ${metadata.accessLevel}`
        });

        // Step 5: Handle access denied
        if (!hasAccess) {
          console.warn('[BlobServe] Access denied');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Step 6: Serve the file
        console.log('[BlobServe] Access granted, serving file');
        serveFile(metadata);

      } catch (err) {
        console.error('[BlobServe] Error:', err);
        setError(err.message || 'An error occurred');
        setLoading(false);
      }
    };

    const serveFile = (metadata) => {
      try {
        // Get file from localStorage
        const storageKey = `file_${hash}`;
        const storedData = localStorage.getItem(storageKey);

        if (!storedData) {
          console.error(`[BlobServe] File not found in localStorage: ${hash}`);
          setError('File data not found in local storage');
          setLoading(false);
          return;
        }

        // Parse the stored data
        const fileData = JSON.parse(storedData);

        // Convert base64 back to blob
        const byteCharacters = atob(fileData.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileData.contentType });

        // Create object URL and serve it
        const blobUrl = URL.createObjectURL(blob);

        console.log('[BlobServe] File served successfully');

        // Redirect to the blob URL so the browser can display/download it
        window.location.replace(blobUrl);

      } catch (error) {
        console.error('[BlobServe] Error serving file:', error);
        setError(`Failed to serve file: ${error.message}`);
        setLoading(false);
      }
    };

    if (hash) {
      checkAccessAndServeFile();
    }
  }, [hash, isAuthenticated, user, checkAccess]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h5" gutterBottom>
          Loading file...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Hash: {hash}
        </Typography>
      </Box>
    );
  }

  // Access denied state
  if (accessDenied) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>

        <Typography variant="h4" gutterBottom color="error">
          Access Denied
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          You don't have permission to view this file.
        </Typography>

        {fileMetadata && (
          <Alert severity="info" sx={{ mt: 2, mb: 3, maxWidth: 600 }}>
            <Typography variant="body2">
              <strong>File:</strong> {fileMetadata.fileName}
              <br />
              <strong>Required Access Level:</strong> {fileMetadata.accessLevel}
              <br />
              <strong>Your Status:</strong> {isAuthenticated ? `${user?.userRole} (Authenticated)` : 'Not Authenticated'}
            </Typography>
          </Alert>
        )}

        {!isAuthenticated ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please sign in to access this file.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              size="large"
            >
              Sign In
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Contact an administrator if you believe you should have access.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </>
        )}
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
        >
          Go to Home
        </Button>
      </Box>
    );
  }

  return null;
};

export default BlobServe;
